import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  CircularProgress,
} from "@mui/material";
import { defineMessages, useIntl } from "react-intl";
import { useMemoryUpdate } from "../../hooks/useMemoryUpdate";
import { useSnackbar } from "@myplant-io/snackbar";
import { MemoryItem } from "../../types";

const messages = defineMessages({
  title: {
    id: "memory.edit.title",
    defaultMessage: "Edit Memory",
  },
  textFieldLabel: {
    id: "memory.edit.textFieldLabel",
    defaultMessage: "Memory Text",
  },
  textFieldHelperText: {
    id: "memory.edit.textFieldHelperText",
    defaultMessage: "Update the memory text",
  },
  cancel: {
    id: "memory.edit.cancel",
    defaultMessage: "Cancel",
  },
  save: {
    id: "memory.edit.save",
    defaultMessage: "Save",
  },
  successMessage: {
    id: "memory.edit.successMessage",
    defaultMessage: "Memory updated successfully",
  },
  errorMessage: {
    id: "memory.edit.errorMessage",
    defaultMessage: "Failed to update memory",
  },
});

type EditMemoryDialogProps = {
  open: boolean;
  memory: MemoryItem | null;
  onClose: () => void;
};

export function EditMemoryDialog({
  open,
  memory,
  onClose,
}: EditMemoryDialogProps) {
  const { formatMessage: t } = useIntl();
  const [text, setText] = useState("");
  const { mutateAsync: updateMemory, isLoading } = useMemoryUpdate();
  const { showSuccessMessage, showErrorMessage } = useSnackbar();

  useEffect(() => {
    if (memory) {
      setText(memory.text);
    }
  }, [memory]);

  const handleSave = async () => {
    const trimmed = text.trim();
    if (!trimmed || !memory) {
      return;
    }

    try {
      await updateMemory({ id: memory._id, text: trimmed });
      showSuccessMessage({
        message: t(messages.successMessage),
      });
      onClose();
    } catch (error) {
      showErrorMessage({
        message: t(messages.errorMessage),
      });
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>{t(messages.title)}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          multiline
          rows={4}
          fullWidth
          label={t(messages.textFieldLabel)}
          helperText={t(messages.textFieldHelperText)}
          value={text}
          onChange={(e) => setText(e.target.value)}
          variant="outlined"
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          {t(messages.cancel)}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!text.trim() || isLoading}
          startIcon={isLoading ? <CircularProgress size={16} /> : null}
        >
          {t(messages.save)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
